import React, { FC, useEffect, useState } from "react";

type MarkdownProps = {
	content?: string;
	className?: string;
};

type MarkdownDeps = {
	ReactMarkdown: typeof import("react-markdown").default;
	rehypeRaw: typeof import("rehype-raw").default;
	remarkGfm: typeof import("remark-gfm").default;
};

type MathPlugins = {
	remarkMath: typeof import("remark-math").default;
	rehypeKatex: typeof import("rehype-katex").default;
};

let depsPromise: Promise<MarkdownDeps> | null = null;
let depsCache: MarkdownDeps | null = null;

let mathPluginsPromise: Promise<MathPlugins> | null = null;
let mathPluginsCache: MathPlugins | null = null;

function loadDeps(): Promise<MarkdownDeps> {
	if (depsCache) {
		return Promise.resolve(depsCache);
	}
	if (!depsPromise) {
		depsPromise = Promise.all([
			import("react-markdown"),
			import("rehype-raw"),
			import("remark-gfm"),
		]).then(([ReactMarkdown, rehypeRaw, remarkGfm]) => {
			depsCache = {
				ReactMarkdown: ReactMarkdown.default,
				rehypeRaw: rehypeRaw.default,
				remarkGfm: remarkGfm.default,
			};
			return depsCache;
		});
	}
	return depsPromise;
}

function loadMathPlugins(): Promise<MathPlugins> {
	if (mathPluginsCache) {
		return Promise.resolve(mathPluginsCache);
	}
	if (!mathPluginsPromise) {
		mathPluginsPromise = Promise.all([
			import("remark-math"),
			import("rehype-katex"),
			// @ts-expect-error - CSS import works at runtime
			import("katex/dist/katex.min.css"),
		]).then(([remarkMath, rehypeKatex]) => {
			mathPluginsCache = {
				remarkMath: remarkMath.default,
				rehypeKatex: rehypeKatex.default,
			};
			return mathPluginsCache;
		});
	}
	return mathPluginsPromise;
}

const Markdown: FC<MarkdownProps> = ({ content = "", className = "" }) => {
	const [deps, setDeps] = useState<MarkdownDeps | null>(depsCache);
	const [mathPlugins, setMathPlugins] = useState<MathPlugins | null>(
		mathPluginsCache
	);

	useEffect(() => {
		if (!deps) {
			loadDeps().then(setDeps);
		}
		if (!mathPlugins) {
			loadMathPlugins().then(setMathPlugins);
		}
	}, []);

	if (!deps) {
		return null;
	}

	const { ReactMarkdown, rehypeRaw, remarkGfm } = deps;

	if (mathPlugins) {
		return (
			<ReactMarkdown
				children={content}
				className={className}
				remarkPlugins={[mathPlugins.remarkMath, remarkGfm]}
				rehypePlugins={[mathPlugins.rehypeKatex, rehypeRaw]}
			/>
		);
	}

	return (
		<ReactMarkdown
			children={content}
			className={className}
			remarkPlugins={[remarkGfm]}
			rehypePlugins={[rehypeRaw]}
		/>
	);
};

export default Markdown;
